import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Animation timeline (ms from start)
const STEP_TIMINGS = {
  leadCard: 400,      // New web lead card slides in
  callingNow: 1600,   // Timer reaches 0:12 — calling now
  callRow: 2600,      // Outbound call row with waveform
  smsOutbound: 4400,  // First SMS bubble (from firm)
  smsReply: 5600,     // Reply SMS bubble (from lead)
  booked: 6800,       // Consultation booked row
  restart: 11000,     // Loop back to start
};

// Simple waveform - matches style used across the site
function Waveform({ color }: { color: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 16 }}>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <Box
          key={i}
          sx={{
            width: 3,
            height: '100%',
            backgroundColor: color,
            borderRadius: 1,
            animation: 'wave 0.8s ease-in-out infinite',
            animationDelay: `${i * 0.1}s`,
            '@keyframes wave': {
              '0%, 100%': { transform: 'scaleY(0.3)' },
              '50%': { transform: 'scaleY(1)' },
            },
          }}
        />
      ))}
    </Box>
  );
}

// Counting timer that runs from 0:00 up to 0:12 while active
function SpeedTimer({ running, done }: { running: boolean; done: boolean }) {
  const [seconds, setSeconds] = React.useState(0);

  React.useEffect(() => {
    if (!running || done) return;
    const interval = setInterval(() => {
      setSeconds((prev) => (prev < 12 ? prev + 1 : prev));
    }, 90);
    return () => clearInterval(interval);
  }, [running, done]);

  const display = done ? 12 : seconds;

  return (
    <Typography
      component="span"
      sx={{
        fontSize: '0.75rem',
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        color: done ? '#27AE60' : '#B5683C',
        transition: 'color 0.3s ease',
      }}
    >
      0:{String(display).padStart(2, '0')}
      {done && ' — calling now'}
    </Typography>
  );
}

// Shared row card wrapper with slide-in animation
function AnimatedRow({
  visible,
  children,
  sx = {},
}: {
  visible: boolean;
  children: React.ReactNode;
  sx?: object;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.75,
        py: 1.25,
        backgroundColor: '#FFFFFF',
        borderRadius: 2,
        border: '1px solid #E8E2D8',
        boxShadow: '0 2px 8px rgba(62, 48, 30, 0.06)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(12px)',
        transition: 'opacity 0.4s ease, transform 0.4s ease',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

// SMS chat bubble
function SmsBubble({
  visible,
  fromFirm,
  text,
}: {
  visible: boolean;
  fromFirm: boolean;
  text: string;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: fromFirm ? 'flex-end' : 'flex-start',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(10px)',
        transition: 'opacity 0.4s ease, transform 0.4s ease',
      }}
    >
      <Typography
        sx={{
          maxWidth: '85%',
          px: 1.5,
          py: 0.75,
          fontSize: '0.72rem',
          lineHeight: 1.45,
          borderRadius: 2.5,
          ...(fromFirm
            ? {
                backgroundColor: '#B5683C',
                color: '#FFFFFF',
                borderBottomRightRadius: 4,
              }
            : {
                backgroundColor: '#F0EAE0',
                color: '#3E301E',
                borderBottomLeftRadius: 4,
              }),
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}

export default function OutboundAnimation() {
  const [step, setStep] = React.useState(0);
  const [cycle, setCycle] = React.useState(0);

  // Steps: 1=lead card, 2=calling now, 3=call row, 4=sms out, 5=sms reply, 6=booked
  React.useEffect(() => {
    const timers = [
      setTimeout(() => setStep(1), STEP_TIMINGS.leadCard),
      setTimeout(() => setStep(2), STEP_TIMINGS.callingNow),
      setTimeout(() => setStep(3), STEP_TIMINGS.callRow),
      setTimeout(() => setStep(4), STEP_TIMINGS.smsOutbound),
      setTimeout(() => setStep(5), STEP_TIMINGS.smsReply),
      setTimeout(() => setStep(6), STEP_TIMINGS.booked),
      setTimeout(() => {
        setStep(0);
        setCycle((c) => c + 1);
      }, STEP_TIMINGS.restart),
    ];
    return () => timers.forEach(clearTimeout);
  }, [cycle]);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minWidth: 0,
        backgroundColor: '#FDFCF9',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 1.25,
        px: { xs: 2.5, md: 4 },
        py: 2,
      }}
    >
      {/* Step 1-2: New web lead card with speed-to-lead timer */}
      <AnimatedRow visible={step >= 1}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            backgroundColor: 'rgba(181, 104, 60, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <LanguageIcon sx={{ fontSize: 20, color: '#B5683C' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#3E301E', lineHeight: 1.3 }}>
            New web lead — Emily R.
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: '#6B5F4D' }}>
            Contact form · Car accident
          </Typography>
        </Box>
        {step >= 1 && <SpeedTimer running={step >= 1} done={step >= 2} />}
      </AnimatedRow>

      {/* Step 3: Outbound call row with waveform */}
      <AnimatedRow visible={step >= 3}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            backgroundColor: step >= 4 ? 'rgba(39, 174, 96, 0.12)' : 'rgba(181, 104, 60, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'background-color 0.3s ease',
          }}
        >
          {step >= 4 ? (
            <CheckCircleIcon sx={{ fontSize: 20, color: '#27AE60' }} />
          ) : (
            <Waveform color="#B5683C" />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#3E301E', lineHeight: 1.3 }}>
            Outbound call — Emily R.
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: '#6B5F4D' }}>
            From your firm's verified number
          </Typography>
        </Box>
        <PhoneForwardedIcon sx={{ fontSize: 18, color: '#B5683C', flexShrink: 0 }} />
      </AnimatedRow>

      {/* Steps 4-5: Two-way SMS thread */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          px: 1.75,
          py: 1.25,
          backgroundColor: '#FFFFFF',
          borderRadius: 2,
          border: '1px solid #E8E2D8',
          boxShadow: '0 2px 8px rgba(62, 48, 30, 0.06)',
          opacity: step >= 4 ? 1 : 0,
          transform: step >= 4 ? 'translateY(0)' : 'translateY(12px)',
          transition: 'opacity 0.4s ease, transform 0.4s ease',
        }}
      >
        <SmsBubble
          visible={step >= 4}
          fromFirm
          text="Hi Emily, this is the intake assistant at Smith Law. Does tomorrow at 2:00 PM work for your consultation?"
        />
        <SmsBubble visible={step >= 5} fromFirm={false} text="Yes, 2 PM works great. Thank you!" />
      </Box>

      {/* Step 6: Consultation booked */}
      <AnimatedRow
        visible={step >= 6}
        sx={{
          backgroundColor: 'rgba(39, 174, 96, 0.08)',
          border: '1px solid rgba(39, 174, 96, 0.3)',
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 22, color: '#27AE60', flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#27AE60', lineHeight: 1.3 }}>
            Consultation booked
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: '#6B5F4D' }}>
            Tomorrow · 2:00 PM · Added to your calendar
          </Typography>
        </Box>
      </AnimatedRow>
    </Box>
  );
}
